import React, { useCallback, useState } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Pagination } from "swiper/modules";
import "swiper/css/pagination";
import "swiper/css/navigation";
import styles from "./TransformativeHome.module.css";
import { Description } from "@/utils/utils";
import { ChevronRight, ArrowRight } from "lucide-react";

interface TitleDescription {
  id: number;
  title: string;
  description: string;
}

interface LinkData {
  id: number;
  label: string;
  url: string;
}

interface Card {
  id: number;
  title_description: TitleDescription;
  link: LinkData;
}

interface TransformativeLearning {
  id: number;
  title_description: TitleDescription;
  cards: Card[];
}

interface Props {
  data: TransformativeLearning;
}

const TransformativeHome: React.FC<Props> = ({ data }) => {
  const { title_description, cards } = data;

  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  const handleAccordionClick = useCallback((id: number) => {
    setActiveAccordion((prev) => (prev === id ? null : id));
  }, []);

  const [hoveredCard, setHoveredCard] = useState<number | null>(
    cards[0]?.id || null
  );

  const MemoizedDescription = React.memo(Description);

  const handleMouseEnter = useCallback((id: number) => setHoveredCard(id), []);
  const handleMouseLeave = useCallback(() => setHoveredCard(null), []);

  return (
    <div
      className={styles.aboutTransformative}
      data-section="Transformative"
      style={{ minHeight: "300px", overflow: "hidden" }}
    >
      <div className="container">
        <div className={styles.TransformativeLearning}>
          <h2 className={styles.mobItem}>{title_description?.title}</h2>
          <p className={styles.mobItem}>
            {title_description?.description || ""}
          </p>
          {/* Desktop View */}
          <div className="row d-none d-md-flex justify-content-center">
            {cards.length > 4 ? (
              <Swiper
                slidesPerView={4}
                spaceBetween={20}
                modules={[Pagination]}
                pagination={{ clickable: true }}
                className={styles.swiperStyle}
              >
                {cards?.map((programme) => (
                  <SwiperSlide
                    key={programme?.id}
                    className={styles.programBox}
                  >
                    <div
                      className={`${styles.card} ${
                        hoveredCard === programme?.id ? styles.hovered : ""
                      }`}
                      onMouseEnter={() => handleMouseEnter(programme?.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <strong>{programme?.title_description?.title}</strong>
                      <MemoizedDescription
                        text={programme?.title_description?.description}
                      />
                      <Link
                        href={programme?.link?.url}
                        target="_blank"
                        className={styles.link}
                        prefetch={false}
                        passHref
                        shallow
                        title={programme?.link?.label}
                      >
                        {programme?.link?.label} <ArrowRight />
                      </Link>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              cards?.map((programme) => (
                <div
                  key={programme?.id}
                  className={`col-md-6 col-xl-3  mb-4 ${styles.programBox}`}
                >
                  <div
                    className={`${styles.card} ${
                      hoveredCard === programme?.id ? styles.hovered : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter(programme?.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <strong>{programme?.title_description?.title}</strong>
                    <MemoizedDescription
                      text={programme?.title_description?.description}
                      style={styles.description}
                    />
                    <Link
                      href={programme?.link?.url}
                      target="_blank"
                      prefetch={false}
                      className={styles.link}
                      passHref
                      shallow
                      title={programme?.link?.label}
                    >
                      {programme?.link?.label} <ArrowRight />
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>{" "}
          {/* Mobile Accordion View */}
          <div className="d-md-none">
            <div className="accordion" id="accordionExample">
              {cards?.map((programme, index) => (
                <div className="accordion-item" key={programme?.id}>
                  <h3
                    className="accordion-header"
                    id={`heading${programme?.id}`}
                  >
                    <Link
                      href={programme?.link?.url}
                      className={styles.link}
                      passHref
                      shallow
                      title={programme?.title_description?.title}
                    >
                      <button
                        className={`accordion-button ${
                          index !== data?.cards?.length - 1
                            ? styles.bottomBorder
                            : ""
                        }`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${programme?.id}`}
                        aria-expanded={activeAccordion === programme?.id}
                        aria-controls={`collapse${programme?.id}`}
                        onClick={() => handleAccordionClick(programme?.id)}
                        aria-hidden="true"
                      >
                        <strong>{programme?.title_description?.title}</strong>
                        <span className={`${styles.accordionIcon} ms-auto`}>
                          <ChevronRight />
                        </span>
                      </button>
                    </Link>
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransformativeHome;
